<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Draggable from 'vuedraggable';
import { emitter } from 'shared/helpers/mitt';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useCrmStore } from '../store';
import { formatMoney, sumAmount } from '../helpers';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import DealCard from '../components/DealCard.vue';
import DealDialog from '../components/DealDialog.vue';
import StagesDialog from '../components/StagesDialog.vue';
import EntryRulesDialog from '../components/EntryRulesDialog.vue';
import ConfirmDialog from 'dashboard/wasapi/common/ConfirmDialog.vue';
import CloseReasonDialog from '../components/CloseReasonDialog.vue';
import AgendaView from '../components/AgendaView.vue';

const { t, locale } = useI18n();
const router = useRouter();
const store = useCrmStore();
const vuexStore = useStore();
const agents = useMapGetter('agents/getVerifiedAgents');

const dealDialogRef = ref(null);
const stagesDialogRef = ref(null);
const closeReasonDialogRef = ref(null);
const pendingMove = ref(null);
const closeReasonKind = ref('won');
const closeReasonRequired = ref(false);
const closeReasonList = ref([]);

const pipelines = computed(() => store.pipelines);
const activePipeline = computed(() => store.activePipeline);
const stages = computed(() => store.stages);
const isLoading = computed(
  () => store.uiFlags.fetchingPipelines || store.uiFlags.fetchingDeals
);
const { isAdmin } = useAdmin();

const ownerOptions = computed(() => [
  { value: '', label: t('WASAPI_CRM.FILTERS.ALL_OWNERS') },
  ...(agents.value || []).map(a => ({ value: a.id, label: a.name })),
]);
const pipelineOptions = computed(() =>
  pipelines.value.map(p => ({ value: p.id, label: p.name }))
);

const selectedPipelineId = computed({
  get: () => store.activePipelineId,
  set: id => store.setActivePipeline(Number(id)),
});

const view = ref('kanban');
const viewOptions = computed(() => [
  {
    value: 'kanban',
    icon: 'i-lucide-columns-3',
    label: t('WASAPI_CRM.VIEW.KANBAN'),
  },
  {
    value: 'agenda',
    icon: 'i-lucide-calendar-days',
    label: t('WASAPI_CRM.VIEW.AGENDA'),
  },
]);

const search = ref('');
const filterAssigneeId = ref('');
const matchesFilters = deal => {
  if (
    filterAssigneeId.value &&
    deal.assigneeId !== Number(filterAssigneeId.value)
  )
    return false;
  if (!search.value.trim()) return true;
  const needle = search.value.trim().toLowerCase();
  return [deal.name, deal.contact?.name, deal.contact?.phoneNumber]
    .filter(Boolean)
    .some(text => String(text).toLowerCase().includes(needle));
};
const isFiltering = computed(
  () => !!search.value.trim() || !!filterAssigneeId.value
);

// local mutable lists per stage so vuedraggable can mutate them
const columns = computed(() =>
  stages.value.map(stage => ({
    stage,
    deals: [...(store.dealsByStage[stage.id] || [])]
      .filter(matchesFilters)
      .sort((a, b) => a.position - b.position || a.id - b.id),
  }))
);

const agendaDeals = computed(() => store.deals.filter(matchesFilters));

const stageTotal = deals =>
  formatMoney(sumAmount(deals), deals[0]?.currency || 'BRL', locale.value);

const totalOpen = computed(() => {
  const openStageIds = new Set(
    stages.value.filter(s => s.category === 'open').map(s => s.id)
  );
  return store.deals
    .filter(d => openStageIds.has(d.stageId))
    .filter(matchesFilters);
});

const commitMove = async (dealId, stageId, position, closeReasonId) => {
  try {
    await store.moveDeal(dealId, { stageId, position, closeReasonId });
  } catch {
    useAlert(t('WASAPI_CRM.DEAL.MOVE_ERROR'));
    store.fetchDeals();
  }
};

const onDealChange = async (stage, event) => {
  const change = event.added || event.moved;
  if (!change) return;
  // Closing into a won/lost stage that has reasons configured → ask for the reason first.
  const reasons = (activePipeline.value?.closeReasons || []).filter(
    r => r.kind === stage.category && r.isActive !== false
  );
  if (
    (stage.category === 'won' || stage.category === 'lost') &&
    reasons.length
  ) {
    pendingMove.value = {
      dealId: change.element.id,
      stageId: stage.id,
      position: change.newIndex,
    };
    closeReasonKind.value = stage.category;
    closeReasonList.value = reasons;
    closeReasonRequired.value =
      stage.category === 'won'
        ? !!activePipeline.value?.requireWonReason
        : !!activePipeline.value?.requireLostReason;
    closeReasonDialogRef.value?.open();
    return;
  }
  await commitMove(change.element.id, stage.id, change.newIndex);
};

const onCloseReasonConfirm = async reasonId => {
  const move = pendingMove.value;
  closeReasonDialogRef.value?.close();
  pendingMove.value = null;
  if (move)
    await commitMove(move.dealId, move.stageId, move.position, reasonId);
};

const onCloseReasonCancel = () => {
  pendingMove.value = null;
  store.fetchDeals(); // revert the optimistic drag
};

const toggleFavorite = async deal => {
  try {
    await store.toggleFavorite(deal);
  } catch {
    useAlert(t('WASAPI_CRM.FAVORITE.ERROR'));
  }
};

const entryRulesDialogRef = ref(null);
const openEntryRules = () => entryRulesDialogRef.value?.open();
const goToReport = () =>
  router.push({
    name: 'wasapi_crm_report',
    params: { pipelineId: store.activePipelineId },
  });

const openNewDeal = stage =>
  dealDialogRef.value?.open({ defaults: { stageId: stage?.id } });
const openDeal = deal => dealDialogRef.value?.open({ deal });

const openStages = () => stagesDialogRef.value?.open(activePipeline.value);
const openNewPipeline = () => stagesDialogRef.value?.open(null);
const duplicatePipeline = async () => {
  try {
    await store.duplicatePipeline(store.activePipelineId);
    useAlert(t('WASAPI_CRM.PIPELINE.DUPLICATED'));
  } catch {
    useAlert(t('WASAPI_CRM.PIPELINE.DUPLICATE_ERROR'));
  }
};

const onPipelineSaved = pipeline => {
  if (pipeline.id !== store.activePipelineId) {
    store.setActivePipeline(pipeline.id);
  } else {
    store.fetchDeals();
  }
};

const confirmRef = ref(null);
const deletePipeline = () => {
  const pipeline = activePipeline.value;
  if (!pipeline || pipeline.default) return;
  confirmRef.value?.open({
    title: t('WASAPI_CRM.PIPELINE.DELETE_CONFIRM', { name: pipeline.name }),
    onConfirm: async () => {
      try {
        await store.deletePipeline(pipeline.id);
        useAlert(t('WASAPI_CRM.PIPELINE.DELETED'));
      } catch (error) {
        useAlert(
          error?.response?.data?.error || t('WASAPI_CRM.PIPELINE.DELETE_ERROR')
        );
      }
    },
  });
};

// Another user or a stage automation changed a deal on this board: refetch. Changes made from
// this session are already on screen, so those pings are skipped.
const currentUserId = useMapGetter('getCurrentUserID');
let refetchTimer = null;
const onRemoteDealChange = data => {
  if (data.pipeline_id !== store.activePipelineId) return;
  if (data.user_id && data.user_id === currentUserId.value) return;
  clearTimeout(refetchTimer);
  refetchTimer = setTimeout(() => store.fetchDeals(), 400);
};

onMounted(async () => {
  vuexStore.dispatch('agents/get');
  emitter.on('wasapi:crm:deal_updated', onRemoteDealChange);
  await store.fetchPipelines();
  await store.fetchDeals();
});
onBeforeUnmount(() => {
  clearTimeout(refetchTimer);
  emitter.off('wasapi:crm:deal_updated', onRemoteDealChange);
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header
      class="flex flex-wrap items-center justify-between gap-3 px-6 py-4 shrink-0"
    >
      <div class="flex items-center gap-3 min-w-0">
        <span class="text-xl font-medium text-n-slate-12 shrink-0">
          {{ t('WASAPI_CRM.TITLE') }}
        </span>
        <div class="w-52 shrink-0">
          <ComboBox
            v-if="pipelines.length"
            v-model="selectedPipelineId"
            :options="pipelineOptions"
          />
        </div>
        <span class="hidden text-sm text-n-slate-11 sm:inline">
          {{
            t('WASAPI_CRM.OPEN_SUMMARY', {
              count: totalOpen.length,
              total: stageTotal(totalOpen),
            })
          }}
        </span>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <div class="flex gap-1 p-1 rounded-lg bg-n-alpha-1 dark:bg-n-solid-3">
          <button
            v-for="option in viewOptions"
            :key="option.value"
            type="button"
            class="flex items-center gap-1.5 px-2.5 py-1 text-sm rounded-md"
            :class="
              view === option.value
                ? 'bg-n-background text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="view = option.value"
          >
            <Icon :icon="option.icon" class="size-4" />
            {{ option.label }}
          </button>
        </div>
        <Input
          v-model="search"
          :placeholder="t('WASAPI_CRM.FILTERS.SEARCH')"
          custom-input-class="h-8 !py-1 text-sm"
          class="w-44"
        />
        <div class="w-48 shrink-0">
          <ComboBox v-model="filterAssigneeId" :options="ownerOptions" />
        </div>
        <Button
          v-tooltip="t('WASAPI_CRM.FAVORITE.FILTER')"
          icon="i-lucide-star"
          :variant="store.onlyFavorites ? 'solid' : 'ghost'"
          :color="store.onlyFavorites ? 'amber' : 'slate'"
          size="sm"
          @click="store.toggleOnlyFavorites()"
        />
        <Button
          v-if="isAdmin"
          v-tooltip="t('WASAPI_CRM.REPORT.OPEN')"
          icon="i-lucide-chart-column"
          variant="ghost"
          color="slate"
          size="sm"
          @click="goToReport"
        />
        <Button
          v-if="isAdmin"
          v-tooltip="t('WASAPI_CRM.ENTRY_RULES.OPEN')"
          icon="i-lucide-log-in"
          variant="ghost"
          color="slate"
          size="sm"
          @click="openEntryRules"
        />
        <Button
          v-if="isAdmin"
          v-tooltip="t('WASAPI_CRM.PIPELINE.NEW')"
          icon="i-lucide-plus-square"
          variant="ghost"
          color="slate"
          size="sm"
          @click="openNewPipeline"
        />
        <Button
          v-if="isAdmin && activePipeline"
          v-tooltip="t('WASAPI_CRM.PIPELINE.DUPLICATE')"
          icon="i-lucide-copy"
          variant="ghost"
          color="slate"
          size="sm"
          @click="duplicatePipeline"
        />
        <Button
          v-if="isAdmin && activePipeline && !activePipeline.default"
          v-tooltip="t('WASAPI_CRM.PIPELINE.DELETE')"
          icon="i-lucide-trash-2"
          variant="ghost"
          color="slate"
          size="sm"
          @click="deletePipeline"
        />
        <Button
          v-if="isAdmin"
          :label="t('WASAPI_CRM.STAGES.MANAGE')"
          icon="i-lucide-settings-2"
          variant="faded"
          color="slate"
          size="sm"
          @click="openStages"
        />
        <Button
          :label="t('WASAPI_CRM.DEAL.NEW')"
          icon="i-lucide-plus"
          color="blue"
          size="sm"
          @click="openNewDeal(stages[0])"
        />
      </div>
    </header>

    <div
      v-if="isLoading && !stages.length"
      class="flex items-center justify-center flex-1"
    >
      <Spinner />
    </div>

    <AgendaView
      v-else-if="view === 'agenda'"
      :deals="agendaDeals"
      :stages="stages"
      @open-deal="openDeal"
    />

    <main
      v-else
      class="flex flex-1 gap-4 px-6 pb-6 overflow-x-auto overflow-y-hidden"
    >
      <div
        v-for="column in columns"
        :key="column.stage.id"
        class="flex flex-col w-72 shrink-0 h-full rounded-2xl bg-n-alpha-1 dark:bg-n-solid-2 outline outline-1 outline-n-weak"
      >
        <div
          class="flex items-center justify-between gap-2 px-3 py-2.5 shrink-0"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span
              class="size-2.5 rounded-full shrink-0"
              :style="{ backgroundColor: column.stage.color }"
            />
            <span class="text-sm font-medium text-n-slate-12 truncate">
              {{ column.stage.name }}
            </span>
            <span class="text-xs text-n-slate-11 shrink-0">
              {{ column.deals.length }}
            </span>
          </div>
          <span class="text-xs text-n-slate-11 shrink-0">
            {{ stageTotal(column.deals) }}
          </span>
        </div>
        <div
          v-if="store.uiFlags.fetchingDeals"
          class="flex justify-center py-6"
        >
          <Spinner class="!size-4" />
        </div>
        <Draggable
          v-else
          :list="column.deals"
          group="wasapi-deals"
          item-key="id"
          animation="150"
          ghost-class="opacity-40"
          :disabled="isFiltering"
          class="flex flex-col flex-1 gap-2 px-2 pb-2 overflow-y-auto min-h-[3rem]"
          @change="onDealChange(column.stage, $event)"
        >
          <template #item="{ element }">
            <DealCard
              :deal="element"
              @open="openDeal"
              @toggle-favorite="toggleFavorite"
            />
          </template>
          <template #footer>
            <button
              type="button"
              class="flex items-center justify-center gap-1 py-2 text-xs rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              @click="openNewDeal(column.stage)"
            >
              <Icon icon="i-lucide-plus" class="size-3.5" />
              {{ t('WASAPI_CRM.DEAL.ADD_HERE') }}
            </button>
          </template>
        </Draggable>
      </div>
    </main>

    <DealDialog ref="dealDialogRef" :stages="stages" />
    <StagesDialog ref="stagesDialogRef" @saved="onPipelineSaved" />
    <CloseReasonDialog
      ref="closeReasonDialogRef"
      :reasons="closeReasonList"
      :kind="closeReasonKind"
      :required="closeReasonRequired"
      @confirm="onCloseReasonConfirm"
      @cancel="onCloseReasonCancel"
    />
    <EntryRulesDialog ref="entryRulesDialogRef" />
    <ConfirmDialog ref="confirmRef" />
  </section>
</template>
